import { CSSProperties } from 'react';
import { Box } from '@mui/material';
import colors from '../../../../assets/theme/base/colors';
import { DeviationsDto } from '../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { formatPercentage, formatVarianceHeadcount, formatVariancePercentage } from './justificationsHelper';

interface DesignatedDisabledPlanSummaryTableProps {
    data: DeviationsDto.HeatmapDesignatedDisabledDto;
}

const tableStyle: CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: colors.white.main,
    tableLayout: 'fixed',
};

const groupHeader: CSSProperties = {
    textAlign: 'center',
    padding: '8px 12px',
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: colors.white.main,
    textTransform: 'uppercase',
    borderRight: `1px solid ${colors.ui.tableHeaderBandBorder}`,
    borderBottom: `1px solid ${colors.ui.tableHeaderBandBorder}`,
    whiteSpace: 'nowrap',
};

const groupHeaderLast: CSSProperties = {
    ...groupHeader,
    borderRight: 'none',
};

const groupCellRowSpan: CSSProperties = {
    textAlign: 'left',
    padding: '8px 16px',
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: colors.white.main,
    textTransform: 'uppercase',
    borderRight: `1px solid ${colors.ui.tableHeaderBandBorder}`,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
};

const subHeader: CSSProperties = {
    textAlign: 'center',
    padding: '6px 8px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: colors.white.main,
    textTransform: 'uppercase',
    lineHeight: 1.25,
    verticalAlign: 'middle',
};

const subHeaderGroupBoundary: CSSProperties = {
    ...subHeader,
    borderRight: `1px solid ${colors.ui.tableHeaderBandBorder}`,
};

const groupBorder: string = `1px solid ${colors.ui.border}`;
const rowBorder: string = `1px solid ${colors.grey[200]}`;

const baseCell: CSSProperties = {
    padding: '8px 6px',
    fontSize: '0.8125rem',
    textAlign: 'center',
    color: colors.dark.main,
    borderTop: rowBorder,
    whiteSpace: 'nowrap',
};

const tintedCell: CSSProperties = {
    ...baseCell,
    backgroundColor: colors.ui.tintedColumnBg,
};

const tintedCellGroupBoundary: CSSProperties = {
    ...tintedCell,
    borderRight: groupBorder,
};

const groupCell: CSSProperties = {
    ...baseCell,
    textAlign: 'left',
    paddingLeft: 16,
    fontWeight: 500,
    borderRight: groupBorder,
};

function varianceTone(value: number | null | undefined): 'negative' | 'neutral' | 'positive' | null {
    if (value === null || value === undefined) return null;
    if (value < 0) return 'negative';
    if (value > 0) return 'positive';
    return 'neutral';
}

function varianceCellStyle(value: number | null | undefined, base: CSSProperties): CSSProperties {
    const tone = varianceTone(value);
    if (tone === null) return { ...base, color: colors.ui.mutedText };
    const palette = colors.deviationVariance[tone];
    return {
        ...base,
        backgroundColor: palette.bg,
        color: palette.cellText,
        fontWeight: 700,
    };
}

export function DesignatedDisabledPlanSummaryTable({ data }: DesignatedDisabledPlanSummaryTableProps) {
    const currentHeadcount: number = data.designatedHeadcount;
    const currentPercentage: number | null = data.currentPercentage;
    const targetPercentage: number | null = data.targetPercentage;
    const targetHeadcount: number = data.targetDesignatedHeadcount;
    const varianceHeadcount: number = currentHeadcount - targetHeadcount;
    const variancePercentage: number | null = data.gapPercentage;

    return (
        <Box sx={{ borderRadius: '8px', border: `1px solid ${colors.ui.border}`, overflow: 'hidden' }}>
            <table style={tableStyle}>
                <colgroup>
                    <col />
                    <col style={{ width: '96px' }} />
                    <col style={{ width: '96px' }} />
                    <col style={{ width: '96px' }} />
                    <col style={{ width: '96px' }} />
                    <col style={{ width: '96px' }} />
                    <col style={{ width: '96px' }} />
                </colgroup>
                <thead style={{ backgroundColor: colors.ui.tableHeaderBand }}>
                    <tr>
                        <th rowSpan={2} style={groupCellRowSpan}>Group</th>
                        <th colSpan={2} style={groupHeader}>Current</th>
                        <th colSpan={2} style={groupHeader}>Target</th>
                        <th colSpan={2} style={groupHeaderLast}>Variance</th>
                    </tr>
                    <tr>
                        <th style={subHeader}>#</th>
                        <th style={subHeaderGroupBoundary}>%</th>
                        <th style={subHeader}>#</th>
                        <th style={subHeaderGroupBoundary}>%</th>
                        <th style={subHeader}>#</th>
                        <th style={subHeader}>%</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={groupCell}>Designated Disabled Employees</td>
                        <td style={tintedCell}>{currentHeadcount}</td>
                        <td style={tintedCellGroupBoundary}>{formatPercentage(currentPercentage)}</td>
                        <td style={baseCell}>{targetHeadcount}</td>
                        <td style={{ ...baseCell, borderRight: groupBorder }}>{formatPercentage(targetPercentage)}</td>
                        <td style={varianceCellStyle(varianceHeadcount, baseCell)}>{formatVarianceHeadcount(varianceHeadcount)}</td>
                        <td style={varianceCellStyle(variancePercentage, baseCell)}>{formatVariancePercentage(variancePercentage)}</td>
                    </tr>
                </tbody>
            </table>
        </Box>
    );
}
